import { firstValueFrom, forkJoin, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { findOrgInfo } from '@degreed/lxp/org/utils';
import {
  BrandingService,
  ConfigurationService,
  LDFlagServiceConfig,
  LocalizationService,
  WebEnvironmentService,
} from '@degreed/lxp/services';
import { AuthService, AuthUser } from '@degreed/lxp/services/authorization';

import {
  LDFlagsService,
  VendorIntegrationsService,
} from '@degreed/lxp/services';
import { OrgInfo } from '@degreed/lxp/services/org';

const mustFetchUser = (authService: AuthService) =>
  authService.isLoggedIn && !authService.authUser;

const getBrandingDataForOrg = (orgInfo: OrgInfo) =>
  orgInfo
    ? {
        orgId: orgInfo.organizationId,
        orgName: orgInfo.name,
        orgImage: orgInfo.image,
        endorsedImage: orgInfo.endorsedImage,
        orgBranding: orgInfo.organizationBranding,
      }
    : undefined;

export const createLdFlagServiceConfig = (
  authService: AuthService,
  envService: WebEnvironmentService
): LDFlagServiceConfig => {
  const authUser = authService.authUser;
  const envString = envService.isProduction ? '' : `-${envService.environment}`;
  const restrictPII =
    authService.authUser?.defaultOrgInfo?.settings.featureFlagsRestrictPII ||
    false;
  const preReleaseGroups =
    authService.authUser?.viewerGroups
      ?.filter((viewerGroup) =>
        viewerGroup.group.name.toLowerCase().includes('pre-release')
      )
      .map((viewerGroup) => viewerGroup.group.name) ?? [];
  return {
    isLoggedIn: authService.isLoggedIn,
    userContextKey: `${authUser?.contextId}${envString}`,
    userProfileKey: restrictPII
      ? null
      : authUser?.viewerProfile.userProfileKey.toString(),
    defaultOrgId: authUser?.defaultOrgId,
    preReleaseGroups: preReleaseGroups,
    clientSideFeatureFlagSdkKey: envService.clientSideFeatureFlagSdkKey,
    featureFlagOverrides: { ...envService.featureFlagOverrides },
  };
};

export function initUser(authService: AuthService) {
  return () => {
    if (mustFetchUser(authService)) {
      return authService.fetchAuthenticatedUser().toPromise();
    }
  };
}

export function initAppState(
  envService: WebEnvironmentService,
  authService: AuthService,
  translate: TranslateService,
  configService: ConfigurationService,
  l10nService: LocalizationService,
  ldService: LDFlagsService,
  brandingService: BrandingService,
  vendorIntegrationService: VendorIntegrationsService
) {
  return () => {
    // Initialize web environment then dependent auth service sequentially
    return firstValueFrom(
      envService.initialize().pipe(
        switchMap(() => configService.init()),
        switchMap(() => authService.fetchAuthenticatedUser(false, true)),
        switchMap(() => {
          const authUser = authService.authUser;
          const orgId = authUser?.defaultOrgId;
          const orgInfo = authUser ? findOrgInfo(authUser, orgId) : null;

          return forkJoin({
            translations: initTranslations(
              authService.authUser,
              translate,
              l10nService
            ),
            ldInitialization: ldService.initialize(
              createLdFlagServiceConfig(authService, envService)
            ),
            branding: from(
              brandingService.loadBranding(
                getBrandingDataForOrg(orgInfo),
                true // reset
              )
            ),
          });
        }),
        switchMap(() => {
          const enableOsano = authService.authUser
            ? ldService.enableOsanoForGoogleAnalytics
            : false;

          const googleAnalyticsEnabled =
            authService.authUser?.defaultOrgInfo?.settings.disableAnalytics ===
              false &&
            !!authService.authUser?.defaultOrgInfo?.settings
              .clientGoogleAnalyticsId;

          return vendorIntegrationService.initialize(
            envService.applicationId,
            enableOsano,
            authService.authUser?.enableOsanoCookieBannerAuthed,
            googleAnalyticsEnabled,
            authService.authUser?.defaultOrgInfo?.settings
              .clientGoogleAnalyticsId
          );
        })
      )
    );
  };
}

/**
 * Initializes the translation service which handles the bulk of our translations
 * but does not cover the initialization of the app locale which will affect
 * certain settings like dates using pipe formatters.
 *
 * Localization is set in app.module.
 *
 * @param translate - translation service to initialize with locales
 * @param localizationService - Only used for the constants
 */
export function initTranslations(
  authUser: AuthUser,
  translate: TranslateService,
  localizationService: LocalizationService
) {
  localizationService.userLocale = authUser?.viewerProfile?.localeId;
  translate.setDefaultLang(localizationService.defaultLocale);
  return translate.use(localizationService.userLocale);
}
